import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedDungeon, DungeonInput, DungeonResult, DungeonMapData } from '@creanizer/types'
import { createIndexedDBStorage, validateAndConvertDungeonImport } from '@creanizer/utils'

const MAX_DUNGEONS = 200  // ダンジョンは詳細データが大きいため200個まで

interface DungeonStore {
  dungeons: SavedDungeon[]
  addDungeon: (input: DungeonInput, result: DungeonResult) => void
  addBatchDungeons: (input: DungeonInput, results: DungeonResult[]) => void
  updateDungeon: (id: string, updates: Partial<SavedDungeon>) => void
  updateDungeonMapData: (id: string, floorIndex: number, mapData: DungeonMapData) => void
  removeDungeon: (id: string) => void
  clearAll: () => void
  exportAsJSON: () => void
  exportAsText: () => void
  exportSingleAsJSON: (id: string) => void
  exportSingleAsText: (id: string) => void
  importFromJSON: (data: any) => void
  getDungeonsByWorld: (worldId: string) => SavedDungeon[]
}

export const useDungeonStore = create<DungeonStore>()(
  persist(
    (set, get) => ({
      dungeons: [],
      
      addDungeon: (input, result) => {
        const newDungeon: SavedDungeon = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          input,
          result,
          createdAt: new Date().toISOString(),
          title: result.basicInfo.name,
          worldId: input.worldContext ? 'current' : undefined
        }
        
        set(state => {
          const dungeons = [newDungeon, ...state.dungeons]
          if (dungeons.length > MAX_DUNGEONS) {
            dungeons.pop() // 古いダンジョンを削除
          }
          return { dungeons }
        })
      },
      
      addBatchDungeons: (input, results) => {
        const newDungeons: SavedDungeon[] = results.map((result, index) => ({
          id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
          input,
          result,
          createdAt: new Date().toISOString(),
          title: result.basicInfo.name,
          worldId: input.worldContext ? 'current' : undefined
        }))
        
        set(state => {
          let dungeons = [...newDungeons, ...state.dungeons]
          if (dungeons.length > MAX_DUNGEONS) {
            dungeons = dungeons.slice(0, MAX_DUNGEONS)
          }
          return { dungeons }
        })
      },
      
      updateDungeon: (id, updates) => {
        set(state => ({
          dungeons: state.dungeons.map(dungeon =>
            dungeon.id === id 
              ? { ...dungeon, ...updates, updatedAt: new Date().toISOString() }
              : dungeon
          )
        }))
      },
      
      updateDungeonMapData: (id, floorIndex, mapData) => {
        set(state => ({
          dungeons: state.dungeons.map(dungeon => {
            if (dungeon.id === id) {
              const updatedMapData = {
                ...(dungeon.mapData || {}),
                [floorIndex]: mapData
              }
              return {
                ...dungeon,
                mapData: updatedMapData,
                updatedAt: new Date().toISOString()
              }
            }
            return dungeon
          })
        }))
      },
      
      removeDungeon: (id) => {
        set(state => ({
          dungeons: state.dungeons.filter(dungeon => dungeon.id !== id)
        }))
      },
      
      clearAll: () => {
        set({ dungeons: [] })
      },
      
      exportAsJSON: () => {
        const { dungeons } = get()
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'dungeons',
          data: dungeons
        }
        
        const dataStr = JSON.stringify(exportData, null, 2)
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
        
        const exportFileDefaultName = `dungeons_${new Date().toISOString().split('T')[0]}.json`
        
        const linkElement = document.createElement('a')
        linkElement.setAttribute('href', dataUri)
        linkElement.setAttribute('download', exportFileDefaultName)
        linkElement.click()
      },
      
      exportAsText: () => {
        const { dungeons } = get()
        let textContent = '=== 保存済みダンジョン一覧 ===\n\n'
        
        dungeons.forEach((dungeon, index) => {
          textContent += `【${index + 1}】${dungeon.title}\n`
          textContent += `作成日: ${new Date(dungeon.createdAt).toLocaleDateString()}\n`
          textContent += `\n[基本情報]\n`
          textContent += `名前: ${dungeon.result.basicInfo.name}\n`
          textContent += `タイプ: ${dungeon.result.basicInfo.type}\n`
          textContent += `難易度: ${dungeon.result.basicInfo.difficulty}\n`
          textContent += `\n[概要]\n${dungeon.result.basicInfo.summary}\n`
          textContent += `\n[説明]\n${dungeon.result.basicInfo.description}\n`
          
          if (dungeon.result.structure) {
            textContent += `\n[構造]\n`
            textContent += `総部屋数: ${dungeon.result.structure.totalRooms}\n`
            textContent += `フロア数: ${dungeon.result.structure.floors.length}\n`
            dungeon.result.structure.floors.forEach(floor => {
              textContent += `\n  フロア${floor.level}: ${floor.theme}\n`
              floor.rooms.forEach(room => {
                textContent += `    - ${room.name}: ${room.description}\n`
              })
            })
          }
          
          textContent += '\n' + '='.repeat(50) + '\n\n'
        })
        
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `dungeons_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id) => {
        const dungeon = get().dungeons.find(d => d.id === id)
        if (!dungeon) return
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'dungeon',
          data: dungeon
        }
        
        const dataStr = JSON.stringify(exportData, null, 2)
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
        const fileName = `${dungeon.title.replace(/[^a-zA-Z0-9ぁ-んァ-ヶー一-龠]/g, '_')}.json`
        
        const linkElement = document.createElement('a')
        linkElement.setAttribute('href', dataUri)
        linkElement.setAttribute('download', fileName)
        linkElement.click()
      },
      
      exportSingleAsText: (id) => {
        const dungeon = get().dungeons.find(d => d.id === id)
        if (!dungeon) return
        
        let textContent = `=== ${dungeon.title} ===\n\n`
        textContent += `作成日: ${new Date(dungeon.createdAt).toLocaleDateString()}\n`
        textContent += `\n[基本情報]\n`
        textContent += `名前: ${dungeon.result.basicInfo.name}\n`
        textContent += `タイプ: ${dungeon.result.basicInfo.type}\n`
        textContent += `難易度: ${dungeon.result.basicInfo.difficulty}\n`
        textContent += `\n[概要]\n${dungeon.result.basicInfo.summary}\n`
        textContent += `\n[説明]\n${dungeon.result.basicInfo.description}\n`
        
        if (dungeon.result.structure) {
          textContent += `\n[構造]\n`
          textContent += `総部屋数: ${dungeon.result.structure.totalRooms}\n`
          textContent += `フロア数: ${dungeon.result.structure.floors.length}\n`
          dungeon.result.structure.floors.forEach(floor => {
            textContent += `\n  フロア${floor.level}: ${floor.theme}\n`
            floor.rooms.forEach(room => {
              textContent += `    - ${room.name}: ${room.description}\n`
            })
          })
          
          if (dungeon.result.structure.transitSpaces?.length) {
            textContent += `\n  [移動空間]\n`
            dungeon.result.structure.transitSpaces.forEach(space => {
              textContent += `    - ${space.name}: ${space.description}\n`
            })
          }
        }
        
        if (dungeon.result.anecdotes) {
          textContent += `\n[逸話]\n`
          textContent += `歴史と伝承:\n${dungeon.result.anecdotes.historyAndLore}\n\n`
          textContent += `噂話:\n${dungeon.result.anecdotes.rumors}\n\n`
          textContent += `過去の出来事:\n${dungeon.result.anecdotes.events}\n\n`
          textContent += `隠された物語:\n${dungeon.result.anecdotes.hiddenStory}\n`
        }
        
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${dungeon.title.replace(/[^a-zA-Z0-9ぁ-んァ-ヶー一-龠]/g, '_')}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },
      
      importFromJSON: (data) => {
        try {
          console.log('[DungeonStore] importFromJSON開始')
          console.log('[DungeonStore] 受信データ:', data)
          console.log('[DungeonStore] データ型:', typeof data)
          console.log('[DungeonStore] 配列チェック:', Array.isArray(data))
          
          // 新形式（dataプロパティを持つ）と旧形式（直接配列）の両方をサポート
          let dungeons: any[] = []
          
          if (data?.data && Array.isArray(data.data)) {
            // 新形式
            console.log('[DungeonStore] 新形式検出: data.data')
            dungeons = data.data
          } else if (Array.isArray(data)) {
            // 旧形式（直接配列）
            console.log('[DungeonStore] 旧形式検出: 直接配列')
            dungeons = data
          } else if (data?.dungeons && Array.isArray(data.dungeons)) {
            // 別の旧形式
            console.log('[DungeonStore] 旧形式検出: data.dungeons')
            dungeons = data.dungeons
          } else {
            console.error('[DungeonStore] 不明なデータ形式:', data)
            throw new Error('インポートデータの形式が不正です')
          }
          
          console.log('[DungeonStore] 抽出されたダンジョン数:', dungeons.length)
          
          // データを検証と変換（旧形式対応）
          console.log('[DungeonStore] validateAndConvertDungeonImport実行前')
          const convertedData = validateAndConvertDungeonImport(dungeons)
          console.log('[DungeonStore] 変換後データ:', convertedData)
          const validDungeons = Array.isArray(convertedData) ? convertedData : []
          console.log('[DungeonStore] 有効なダンジョン数:', validDungeons.length)
          
          // データ形式の検証と変換
          const processedDungeons = validDungeons
            .filter((dungeon: any) => dungeon?.result?.basicInfo)
            .map((dungeon: any) => ({
              ...dungeon,
              id: dungeon.id || `imported_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              createdAt: dungeon.createdAt || new Date().toISOString(),
              title: dungeon.title || dungeon.result.basicInfo.name
            }))
          
          // 現在のダンジョンと重複チェック（名前とテーマで判定）
          const currentDungeons = get().dungeons
          const uniqueDungeons = processedDungeons.filter((newDungeon: any) => {
            return !currentDungeons.some(existing => 
              existing.result.basicInfo.name === newDungeon.result.basicInfo.name && 
              existing.input?.theme === newDungeon.input?.theme
            )
          })
          
          // 既存データにマージ（最大数を超えたら古いものを削除）
          let mergedDungeons = [...uniqueDungeons, ...currentDungeons]
          if (mergedDungeons.length > MAX_DUNGEONS) {
            mergedDungeons = mergedDungeons.slice(0, MAX_DUNGEONS)
          }
          
          console.log('[DungeonStore] 最終マージ数:', mergedDungeons.length)
          set({ dungeons: mergedDungeons })
          console.log('[DungeonStore] set完了')
          
          // インポート結果を通知
          const skippedCount = processedDungeons.length - uniqueDungeons.length
          if (uniqueDungeons.length > 0) {
            alert(`ダンジョンを${uniqueDungeons.length}件インポートしました。${skippedCount > 0 ? `（${skippedCount}件の重複をスキップ）` : ''}`)
          }
        } catch (error) {
          console.error('[DungeonStore] インポートエラー:', error)
          console.error('[DungeonStore] エラースタック:', (error as Error).stack)
          throw error
        }
      },
      
      getDungeonsByWorld: (worldId) => {
        const { dungeons } = get()
        return dungeons.filter(d => d.worldId === worldId)
      }
    }),
    {
      name: 'dungeon-storage',
      version: 1,
      storage: createIndexedDBStorage('dungeon-storage'),
      partialize: (state) => ({ dungeons: state.dungeons })
    }
  )
)